import React, { useState } from 'react';
import { Button } from 'rsuite';
import Whisper from 'rsuite/esm/Whisper';
import IconButton from 'rsuite/esm/IconButton';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import Popover from 'rsuite/esm/Popover';
import Dropdown from 'rsuite/esm/Dropdown';
import { ModalsService } from '../../../services/modals.service';
import { AlertService } from '../../../services/alert.service';
import { PhoneInput } from '../users/PhoneInput';
import { useGlobalMutate } from '../../../api/useGlobalMutate';
import { SmsTemplateModal } from './sms-template-modal';
import { useCurrentUser } from '../../current-user';
import { userIsAdmin, userIsSalesManager } from '../../../utils/common.utils';
import useSWR from 'swr';
import { RouterInput, trpc } from '../../../api/trpc';
import useSWRMutation from 'swr/mutation';
import { User, UserSelect } from '../../common/user-select';

export const SMS_LIMIT = 70;

export function calculateSmsCount(length: number): number {
  return Math.max(1, Math.ceil(length / SMS_LIMIT));
}

export default function SmsSender() {
  const currentUser = useCurrentUser();
  const isSalesManager = userIsSalesManager(currentUser.profile);
  const isAdmin = userIsAdmin(currentUser.profile);

  const { mutate: globalMutate } = useGlobalMutate();
  const [phone, setPhone] = useState('');
  const [text, setText] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const {
    data: templates,
    isLoading,
    mutate,
  } = useSWR(['smsRouter.template.list'], () =>
    trpc.smsRouter.template.list.query(),
  );

  const smsCount = calculateSmsCount(text.length);

  const addTemplate = () => {
    ModalsService.createModal(SmsTemplateModal, { mutate });
  };

  const { trigger: deleteTemplate } = useSWRMutation(
    ['smsRouter.template.delete'],
    (_key, { arg }: { arg: RouterInput['smsRouter']['template']['delete'] }) =>
      trpc.smsRouter.template.delete.mutate(arg),
  );

  const { trigger: sendSms, isMutating: isSending } = useSWRMutation(
    ['smsRouter.sender.sendSms'],
    (_key, { arg }: { arg: RouterInput['smsRouter']['sender']['sendSms'] }) =>
      trpc.smsRouter.sender.sendSms.mutate(arg),
  );

  const handleSend = async () => {
    const targetPhone = selectedUser?.phone ?? phone;
    const cleanPhone = targetPhone.replace(/[^\d]/g, '');

    if (!cleanPhone || !text) {
      AlertService.error('Введите номер телефона и текст сообщения');
      return;
    }

    try {
      await sendSms({ phone: cleanPhone, text, clientId: selectedUser?.id });
      AlertService.success(`SMS успешно отправлено на номер ${cleanPhone}`);
      setPhone('');
      setText('');
      setSelectedUser(null);
      globalMutate();
    } catch (e) {
      AlertService.error('Ошибка при отправке SMS');
      console.error(e);
    }
  };

  const renderMenu =
    (template: { id: string; name: string; text: string }): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = async (
        eventKey: string | undefined,
        event: React.SyntheticEvent,
      ) => {
        event.stopPropagation();
        onClose();

        if (eventKey === 'edit') {
          ModalsService.createModal(SmsTemplateModal, {
            idTemplate: template.id,
            mutate,
          });
        }

        if (eventKey === 'delete') {
          try {
            await deleteTemplate({ id: template.id });
            mutate();
          } catch (e) {
            AlertService.error('Ошибка удаления шаблона');
            console.error('Ошибка удаления шаблона', e);
          }
        }
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey="edit">
              <span className="ps-2">Редактировать</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey="delete">
              <span className="ps-2 text-danger">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  return (
    <div className="row flex-row justify-content-between">
      <div className="col-md-6">
        <div className="mb-3">
          <UserSelect
            name="client"
            placeholder="Выберите клиента"
            isClearable
            value={selectedUser}
            onChange={(user: User | null) => setSelectedUser(user)}
          />
        </div>

        <div className="mb-3">
          <PhoneInput
            placeholder="Введите номер телефона"
            value={selectedUser?.phone ?? phone}
            onChange={(value: string) => setPhone(value)}
            disabled={!!selectedUser}
          />
        </div>

        <div className="text-muted mb-2">
          Текст длина: {text.length} / {SMS_LIMIT} символов • {smsCount} смс
        </div>

        <div className="mb-3">
          <textarea
            className="form-control"
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        {isSalesManager && (
          <Button
            as="button"
            className="btn btn-success"
            onClick={handleSend}
            disabled={isSending}
          >
            {isSending ? 'Отправляем...' : 'Отправить'}
          </Button>
        )}
      </div>

      <div className="col-md-4 mt-3">
        <div className="list-group">
          {templates?.length ? (
            templates.map((template) => (
              <div
                key={template.id}
                className="list-group-item d-flex align-items-center justify-content-between"
              >
                <div
                  className="d-flex align-items-center gap-2 w-75 text-success cursor-pointer"
                  onClick={() => setText(template.text)}
                >
                  <span>{template.name}</span>
                </div>
                {isAdmin && (
                  <Whisper
                    placement="leftStart"
                    trigger="click"
                    onClick={(e) => e.stopPropagation()}
                    speaker={renderMenu(template)}
                  >
                    <IconButton
                      appearance="subtle"
                      icon={<i className="bi bi-three-dots" />}
                    />
                  </Whisper>
                )}
              </div>
            ))
          ) : (
            <div className="text-muted p-2">
              {isLoading ? 'Загрузка...' : 'Шаблонов нет'}{' '}
            </div>
          )}
        </div>

        {isAdmin && (
          <Button
            as="button"
            className="w-100 btn btn-success mt-3"
            onClick={addTemplate}
          >
            Создать шаблон
          </Button>
        )}
      </div>
    </div>
  );
}
